import React, { useState } from "react";
import ReactDOM from "react-dom";
import { authFetch, api } from "../utils/api";
import { useApp } from "../context/AppContext";
import { t } from "../utils/i18n";
import { compressImage } from "../utils/imageCompress";
import { Image as ImageIcon, Video as VideoIcon } from "lucide-react";
import SponsorModal from "./SponsorModal";

// Étape 1 (média + légende) puis étape 2 (ciblage, via le SponsorModal générique déjà utilisé
// pour booster un lieu/publication/Live — on lui donne juste target_type="ad_creative").
export default function AdCreativeUpload({ onClose }: { onClose: () => void }) {
  const { lang } = useApp();
  const [file, setFile] = useState<{ url: string; media_type: "image" | "video" } | null>(null);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [adCreativeId, setAdCreativeId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const handleSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    setError("");
    try {
      const isVideo = f.type.startsWith("video/");
      const toSend = isVideo ? f : await compressImage(f);
      const fd = new FormData();
      fd.append("file", toSend);
      const res = await authFetch("/uploads/publication", { method: "POST", body: fd });
      const data = await res.json();
      if (data?.url) setFile({ url: data.url, media_type: isVideo ? "video" : "image" });
      else setError("Erreur lors de l'envoi du média");
    } catch {
      setError("Erreur lors de l'envoi du média");
    } finally {
      setUploading(false);
    }
  };

  const proceed = async () => {
    if (!file) return;
    setCreating(true);
    setError("");
    try {
      const data = await api.post("/sponsorships/ad-creatives", { media_url: file.url, media_type: file.media_type, caption: caption.trim() || undefined });
      if (data?.id) setAdCreativeId(data.id);
      else setError(data?.error || "Erreur lors de la création de la publicité");
    } catch {
      setError("Erreur de connexion");
    } finally {
      setCreating(false);
    }
  };

  if (adCreativeId) {
    return <SponsorModal targetType="ad_creative" targetId={adCreativeId} targetLabel={caption.trim() || t(lang, "ad_creative_target_label")} onClose={onClose} />;
  }

  // Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un position:fixed
  // descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:"touch") se positionne
  // sur Safari iOS par rapport au contenu défilant plutôt qu'au vrai viewport.
  return ReactDOM.createPortal(
    <div onClick={onClose} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 2000, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: "24px 24px 0 0", padding: 24, width: "100%", maxWidth: 480, maxHeight: "88vh", overflowY: "auto", boxSizing: "border-box" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <span style={{ fontSize: 18, fontWeight: 800 }}>{t(lang, "ad_tool_create_ad_cta")}</span>
          <button onClick={onClose} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#999" }}>✕</button>
        </div>
        <p style={{ fontSize: 12.5, color: "#999", marginBottom: 18 }}>{t(lang, "ad_tool_diffuse_sub")}</p>

        {!file ? (
          <label style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "40px 20px", borderRadius: 16, border: "1.5px dashed #E0E0E0", cursor: "pointer", backgroundColor: "#FAFAFA" }}>
            <input type="file" accept="image/*,video/*" onChange={handleSelect} style={{ display: "none" }} />
            {uploading ? (
              <span style={{ fontSize: 13, color: "#999" }}>Envoi en cours…</span>
            ) : (
              <>
                <div style={{ display: "flex", gap: 8 }}>
                  <ImageIcon size={22} color="#999" strokeWidth={1.6} />
                  <VideoIcon size={22} color="#999" strokeWidth={1.6} />
                </div>
                <span style={{ fontSize: 14, fontWeight: 700 }}>{t(lang, "ad_tool_create_ad_title")}</span>
                <span style={{ fontSize: 12, color: "#999" }}>{t(lang, "ad_tool_create_ad_desc")}</span>
              </>
            )}
          </label>
        ) : (
          <>
            <div style={{ borderRadius: 16, overflow: "hidden", backgroundColor: "#F5F5F5", marginBottom: 14 }}>
              {file.media_type === "video" ? (
                <video src={file.url} controls style={{ width: "100%", maxHeight: 280, display: "block" }} />
              ) : (
                <img src={file.url} alt="" style={{ width: "100%", maxHeight: 280, objectFit: "contain", display: "block" }} />
              )}
            </div>
            <input
              value={caption} onChange={e => setCaption(e.target.value.slice(0, 200))}
              placeholder="Légende (optionnel)"
              style={{ width: "100%", padding: "12px 14px", borderRadius: 12, border: "1px solid #E0E0E0", fontSize: 14, marginBottom: 16, boxSizing: "border-box" }}
            />
            {!!error && <div style={{ color: "#c0392b", fontSize: 13, marginBottom: 12 }}>{error}</div>}
            <button onClick={proceed} disabled={creating} style={{ width: "100%", padding: "14px", borderRadius: 14, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 15, fontWeight: 700, cursor: creating ? "default" : "pointer", opacity: creating ? 0.7 : 1 }}>
              {creating ? "…" : "Continuer"}
            </button>
          </>
        )}
        {!!error && !file && <div style={{ color: "#c0392b", fontSize: 13, marginTop: 12 }}>{error}</div>}
      </div>
    </div>
  ,
    document.body
  );
}
